import { createAsyncThunk } from "@reduxjs/toolkit"
import { AppThunkAPIType } from "../../../app/appTypes"
import { deleteWithAuth, getWithAuth, postWithAuth, putWithAuth } from "../../../http"

export enum DepartmentNotificationTargetEnum {
    MembersOnly = "MembersOnly",
    DepartmentEmailOnly = "DepartmentEmailOnly",
    MembersAndDepartmentEmail = "MembersAndDepartmentEmail"
}

export interface CompanyDepartmentHeader {
    id: number
    code: string
    name: string
    description: string
    isActive: boolean
    displayOrder: number
    color: string
    parentDepartmentId?: number | null
    email: string
    notificationTargetType: DepartmentNotificationTargetEnum
}

export interface CompanyDepartmentDetails extends CompanyDepartmentHeader {
    members: CompanyDepartmentMember[]
}

export interface CompanyDepartmentMember {
    id: number
    userName: string
    firstName?: string
    lastName?: string
    email?: string
    isActive: boolean
    isNotificationReceiver: boolean
}


export interface SaveCompanyDepartment {
    code: string
    name: string
    description: string
    isActive: boolean
    displayOrder: number
    color: string
    parentDepartmentId: number | null
    email: string
    notificationTargetType: DepartmentNotificationTargetEnum

}

export interface SaveCompanyDepartmentMemberDto {
    userName: string;
    isNotificationReceiver: boolean;
}

export interface SetCompanyDepartmentMembersDto {
    members: SaveCompanyDepartmentMemberDto[];
}


export interface CompanyDepartmentMemberDto {
    id: number;
    userName: string;
    isActive: boolean;
    isNotificationReceiver: boolean;
}

export const loadCompanyDepartments = createAsyncThunk<
    CompanyDepartmentDetails[],
    void,
    AppThunkAPIType
>(
    "companyDepartments/loadAll",
    async (_, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/company-departments` }));
        const { payload } = result;

        if (getWithAuth.fulfilled.match(result)) {
            return payload as CompanyDepartmentDetails[];
        } else {
            return rejectWithValue(payload ?? { kind: "unknown" });
        }
    }
);

export const loadCompanyDepartmentById = createAsyncThunk<
    CompanyDepartmentHeader,
    number,
    AppThunkAPIType
>(
    "companyDepartments/loadById",
    async (id, { dispatch, rejectWithValue }) => {
        const result = await dispatch(getWithAuth({ url: `api/company-departments/${id}` }));
        const { payload } = result;

        if (getWithAuth.fulfilled.match(result)) {
            return payload as CompanyDepartmentHeader;
        } else {
            return rejectWithValue(payload ?? { kind: "unknown" });
        }
    }
);

export const createCompanyDepartment = createAsyncThunk<
    CompanyDepartmentDetails,
    SaveCompanyDepartment,
    AppThunkAPIType
>(
    "companyDepartments/create",
    async (dto, { dispatch, rejectWithValue }) => {

        const result = await dispatch(postWithAuth({
            url: `api/company-departments`,
            payload: dto
        }));

        const { payload } = result;

        if (postWithAuth.fulfilled.match(result)) {
            return payload as CompanyDepartmentDetails;
        } else {
            return rejectWithValue(payload ?? { kind: "unknown" });
        }
    }
);

export const updateCompanyDepartment = createAsyncThunk<
    CompanyDepartmentDetails,
    { id: number; dto: SaveCompanyDepartment },
    AppThunkAPIType
>(
    "companyDepartments/update",
    async ({ id, dto }, { dispatch, rejectWithValue }) => {

        const result = await dispatch(putWithAuth({
            url: `api/company-departments/${id}`,
            payload: dto
        }));

        const { payload } = result;

        if (putWithAuth.fulfilled.match(result)) {
            return payload as CompanyDepartmentDetails;
        } else {
            return rejectWithValue(payload ?? { kind: "unknown" });
        }
    }
);

export const deleteCompanyDepartment = createAsyncThunk<
    number,
    { id: number; hardDelete?: boolean },
    AppThunkAPIType
>(
    "companyDepartments/delete",
    async ({ id, hardDelete = false }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(
            deleteWithAuth({
                url: `api/company-departments/${id}?hardDelete=${hardDelete}`
            })
        );

        const { payload } = result;

        if (deleteWithAuth.fulfilled.match(result)) {
            return id;
        } else {
            return rejectWithValue(payload ?? { kind: "unknown" });
        }
    }
);

export const setCompanyDepartmentMembers = createAsyncThunk<
    CompanyDepartmentMemberDto[],
    { id: number; dto: SetCompanyDepartmentMembersDto },
    AppThunkAPIType
>(
    "companyDepartments/setMembers",
    async ({ id, dto }, { dispatch, rejectWithValue }) => {
        const result = await dispatch(
            putWithAuth({
                url: `api/company-departments/${id}/members`,
                payload: dto
            })
        );

        const { payload } = result;

        if (putWithAuth.fulfilled.match(result)) {
            return payload as CompanyDepartmentMemberDto[];
        } else {
            return rejectWithValue(payload ?? { kind: "unknown" });
        }
    }
);